import { css } from "@compiled/react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { Row } from "@/libraries/wpl/foundations/src";

import { Link } from "../../../../components/wpl/link/src/index";

import type { MessageDescriptor } from "react-intl";

const containerStyle = css({
    alignItems: "stretch",
    background: "#E9F2FE",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    letterSpacing: "0.3px",
    margin: "-40px 0 0 0",
    display: "flex",
    flexDirection: "row",
    textAlign: "center",
    borderRadius: "4px",

    [media.above.lg]: {
        borderRadius: 0,
        height: "141px"
    }
});

const contentContainerStyle = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "row",

    [media.below.lg]: {
        flexDirection: "column",
        padding: "25px 0"
    }
});

const logoStyle = css({
    background:
        "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/team24-color-lockup-desktop-3939666149.webp) center / contain no-repeat",
    height: "100%",
    margin: "0 50px 0 0",
    width: "270px",

    [media.below.lg]: {
        display: "none"
    }
});

const headerStyle = css({
    margin: "0px 16px 0px 16px",
    textAlign: "center",
    lineHeight: "1.33333333",
    fontWeight: 500,
    fontSize: "1.5rem",

    [media.above.lg]: {
        display: "none"
    }
});

const lgHeaderStyle = css({
    fontWeight: 500,
    lineHeight: "1.33333333",
    textAlign: "left",

    [media.below.lg]: {
        display: "none"
    }
});

const descriptionStyle = css({
    fontSize: "20px",
    lineHeight: "1.33333333",
    fontWeight: "400",

    [media.above.lg]: {
        fontSize: "14px",
        // eslint-disable-next-line @atlaskit/design-system/no-physical-properties
        marginBottom: 0,
        textAlign: "left"
    }
});

const linkStyle = css({
    fontSize: "1.1rem",
    [media.above.lg]: {
        flexBasis: "30%"
    }
});

const leftAccentStyle = css({
    [media.above.lg]: {
        flexBasis: "25%",
        background:
            "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/team24-liftedbar-left-desktop-1497436778.webp) left / cover no-repeat"
    },
    [media.below.lg]: {
        flexBasis: "30%",
        background:
            "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/team24-liftedbar-left-tablet-3665178386.webp) right / cover no-repeat"
    }
});

const rightAccentStyle = css({
    [media.above.lg]: {
        flexBasis: "25%",
        background:
            "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/team24-liftedbar-right-desktop-2027904593.webp) right / cover no-repeat"
    },
    [media.below.lg]: {
        flexBasis: "30%",
        background:
            "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/team24-liftedbar-right-tablet-1999618345.webp) left / cover no-repeat"
    }
});

type Props = {
    url_ref?: string;
    utm_source?: string;
    utm_campaign?: string;
    utm_content: string;
    utm_medium: string;
    messages?: Record<string, MessageDescriptor>;
};

export const TeamBanner = ({
    url_ref = "Team24",
    utm_source = "marketing",
    utm_campaign = "acquisition",
    utm_content,
    utm_medium,
    messages: messageOverrides
}: Props) => {
    const intl = useIntl();

    const messages = defineMessages({
        header: {
            id: "LiftedBar.Team.24.header",
            defaultMessage: "Our annual conference is back in Las Vegas!"
        },
        description: {
            id: "LiftedBar.Team.24.description",
            defaultMessage:
                "Be the first to see our latest innovations. For a limited time only, save $1000 on your all-access pass."
        },
        linkText: {
            id: "LiftedBar.Team.24.link",
            defaultMessage: "Register now"
        },
        ...(messageOverrides ? messageOverrides : {})
    });

    const linkText = intl.formatMessage(messages.linkText);

    const url = `https://events.atlassian.com/team?ref=${url_ref}&utm_source=${utm_source}&utm_campaign=${utm_campaign}&utm_content=${utm_content}&utm_medium=${utm_medium}`;

    return (
        <Row layout="12" alignItems="center">
            <div css={containerStyle}>
                <div css={leftAccentStyle}> </div>
                <div css={contentContainerStyle}>
                    <div css={logoStyle}> </div>
                    <div css={headerStyle}>
                        <span>
                            <FormattedMessage {...messages.header} />
                        </span>
                    </div>
                    <div>
                        <div css={lgHeaderStyle}>
                            <span>
                                <FormattedMessage {...messages.header} />
                            </span>
                        </div>
                        <p css={descriptionStyle}>
                            <FormattedMessage {...messages.description} />
                        </p>
                    </div>
                    <div css={linkStyle}>
                        <Link
                            href={url}
                            text={linkText}
                            type="basic-underline"
                            customLinkColor="#0052cc"
                            doesArrowAnimate={true}
                            dataEventProps={{
                                "data-event-component": "linkButton",
                                "data-event-container": "banner-link-button",
                                "data-event": "clicked",
                                "data-product-key": "jiraSoftware"
                            }}
                        />
                    </div>
                </div>
                <div css={rightAccentStyle}> </div>
            </div>
        </Row>
    );
};
